<template>
  <div class="settings-page">
    <div class="settings-header">
      <h1 class="page-title">{{ $t('admin.settings.title') }}</h1>
      <p class="page-subtitle">{{ $t('admin.settings.subtitle') }}</p>
    </div>

    <!-- Section navigation -->
    <div class="settings-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        class="settings-tab"
        :class="{ active: activeTab === tab.id }"
        type="button"
        @click="switchTab(tab.id)"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="loading-panel">
      <span class="spinner"></span>
      {{ $t('admin.settings.loading') }}
    </div>

    <!-- License Tab -->
    <div v-else-if="activeTab === 'license'">
      <div v-if="licenseLoading" class="loading-panel">
        <span class="spinner"></span>
        {{ $t('admin.settings.license_loading') }}
      </div>

      <template v-else>
        <!-- Grace period banner -->
        <div v-if="licenseStatus?.status === 'grace'" class="license-banner license-banner-warning">
          {{ $t('admin.settings.license_grace_banner', { days: licenseStatus.grace_days_remaining }) }}
        </div>

        <!-- Inactive/expired banner -->
        <div
          v-else-if="licenseStatus && ['inactive', 'expired'].includes(licenseStatus.status)"
          class="license-banner license-banner-error"
        >
          {{ $t('admin.settings.license_inactive_banner') }}
        </div>

        <!-- Active / Grace details card -->
        <div
          v-if="licenseStatus && ['active', 'grace'].includes(licenseStatus.status)"
          class="card"
        >
          <div class="license-details-header">
            <h3 class="card-title" style="margin-bottom: 0; padding-bottom: 0; border-bottom: none">
              {{ $t('admin.settings.license_details_title') }}
            </h3>
            <span
              class="license-badge"
              :class="licenseStatus.status === 'active' ? 'badge-active' : 'badge-grace'"
            >
              {{ licenseStatus.status === 'active' ? $t('admin.settings.license_status_active') : $t('admin.settings.license_status_grace') }}
            </span>
          </div>
          <div class="license-info-grid">
            <div class="license-info-item">
              <span class="license-info-label">{{ $t('admin.settings.license_software_version') }}</span>
              <span class="license-info-value">v{{ appVersion }}</span>
            </div>
            <div class="license-info-item">
              <span class="license-info-label">{{ $t('admin.settings.license_key_label') }}</span>
              <span class="license-info-value">{{ licenseStatus.license_key || '—' }}</span>
            </div>
            <div class="license-info-item">
              <span class="license-info-label">{{ $t('admin.settings.license_domain_label') }}</span>
              <span class="license-info-value">{{ licenseStatus.domain || '—' }}</span>
            </div>
            <div class="license-info-item">
              <span class="license-info-label">{{ $t('admin.settings.license_activated_label') }}</span>
              <span class="license-info-value">{{
                licenseStatus.activated_at
                  ? new Date(licenseStatus.activated_at).toLocaleDateString()
                  : '—'
              }}</span>
            </div>
            <div class="license-info-item">
              <span class="license-info-label">{{ $t('admin.settings.license_last_verified_label') }}</span>
              <span class="license-info-value">{{
                licenseStatus.last_verified_at
                  ? new Date(licenseStatus.last_verified_at).toLocaleDateString()
                  : '—'
              }}</span>
            </div>
          </div>
          <div class="license-actions">
            <button
              class="btn btn-secondary"
              :disabled="verifying"
              type="button"
              @click="verifyLicense"
            >
              {{ verifying ? $t('admin.settings.btn_verifying') : $t('admin.settings.btn_verify') }}
            </button>
            <button
              class="btn btn-danger"
              :disabled="deactivating"
              type="button"
              @click="showDeactivateConfirm = true"
            >
              {{ $t('admin.settings.btn_deactivate') }}
            </button>
          </div>

          <!-- Software update notification -->
          <div v-if="updateCheckResult?.update_available" class="update-notification">
            <span class="update-badge">{{ $t('admin.settings.update_badge') }}</span>
            <span class="update-version">v{{ updateCheckResult.latest_version }}</span>
            <a
              v-if="updateCheckResult.download_url"
              :href="updateCheckResult.download_url"
              target="_blank"
              rel="noopener noreferrer"
              class="update-download-link"
            >{{ $t('admin.settings.update_download') }}</a>
            <span class="update-hint">
              {{ $t('admin.settings.update_hint') }}
            </span>
          </div>
        </div>

        <!-- Activation form (shown when no active license) -->
        <div
          v-if="!licenseStatus || ['inactive', 'expired'].includes(licenseStatus.status)"
          class="card"
        >
          <h3 class="card-title">{{ $t('admin.settings.activate_title') }}</h3>
          <p class="card-desc">
            {{ $t('admin.settings.activate_desc') }}
          </p>
          <div class="form-group">
            <label class="form-label">{{ $t('admin.settings.license_key_label') }}</label>
            <input
              v-model="licenseKeyInput"
              type="text"
              class="form-input"
              :placeholder="$t('admin.settings.license_key_placeholder')"
            />
            <span class="form-hint">{{ $t('admin.settings.license_key_hint') }}</span>
          </div>
          <div v-if="licenseError" class="save-feedback error">{{ licenseError }}</div>
          <div class="card-footer">
            <button
              class="btn btn-primary"
              :disabled="activating || !licenseKeyInput.trim()"
              type="button"
              @click="activateLicense"
            >
              {{ activating ? $t('admin.settings.btn_activating') : $t('admin.settings.btn_activate') }}
            </button>
          </div>
        </div>

        <!-- Deactivate confirmation dialog -->
        <div
          v-if="showDeactivateConfirm"
          class="modal-overlay"
          @click.self="showDeactivateConfirm = false"
        >
          <div class="modal-box">
            <h4 class="modal-title">{{ $t('admin.settings.deactivate_modal_title') }}</h4>
            <p class="modal-text">
              {{ $t('admin.settings.deactivate_modal_text') }}
            </p>
            <div class="modal-actions">
              <button
                class="btn btn-secondary"
                type="button"
                @click="showDeactivateConfirm = false"
              >
                {{ $t('admin.settings.btn_cancel') }}
              </button>
              <button
                class="btn btn-danger"
                :disabled="deactivating"
                type="button"
                @click="deactivateLicense"
              >
                {{ deactivating ? $t('admin.settings.btn_deactivating') : $t('admin.settings.btn_yes_deactivate') }}
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Theme Configuration -->
    <div v-else-if="activeTab === 'theme'">
      <ThemeAdminConfig />
    </div>

    <!-- General Tab -->
    <div v-else-if="activeTab === 'general'">
      <div class="card">
        <h3 class="card-title">{{ $t('admin.settings.general_info_title') }}</h3>
        <div class="form-group">
          <label class="form-label">{{ $t('admin.settings.field_site_name') }}</label>
          <input v-model="form.site_name" type="text" class="form-input" placeholder="My Casino" />
          <span class="form-hint">{{ $t('admin.settings.hint_site_name') }}</span>
        </div>
        <div class="form-group">
          <label class="form-label">{{ $t('admin.settings.field_site_description') }}</label>
          <textarea
            v-model="form.site_description"
            class="form-input form-textarea"
            rows="2"
            maxlength="160"
            placeholder="Your online casino with provably fair games, instant payouts, and exciting bonuses."
          ></textarea>
          <span class="form-hint">{{ $t('admin.settings.hint_site_description') }} {{ (form.site_description || '').length }}/160</span>
        </div>
        <div class="form-group">
          <label class="form-label">{{ $t('admin.settings.field_og_image') }}</label>
          <input v-model="form.site_og_image" type="text" class="form-input" placeholder="https://example.com/og-image.jpg" />
          <span class="form-hint">{{ $t('admin.settings.hint_og_image') }}</span>
        </div>
        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">{{ $t('admin.settings.field_default_currency') }}</label>
            <select v-model="form.default_currency" class="form-input">
              <option value="USD">USD — US Dollar</option>
              <option value="EUR">EUR — Euro</option>
              <option value="GBP">GBP — British Pound</option>
              <option value="CAD">CAD — Canadian Dollar</option>
              <option value="AUD">AUD — Australian Dollar</option>
              <option value="BTC">BTC — Bitcoin</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">{{ $t('admin.settings.field_minimum_age') }}</label>
            <input
              v-model.number="form.minimum_age"
              type="number"
              class="form-input"
              min="18"
              max="25"
              placeholder="18"
            />
            <span class="form-hint">{{ $t('admin.settings.hint_minimum_age') }}</span>
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">{{ $t('admin.settings.field_logo') }}</label>
          <div class="upload-box" @click="triggerLogoUpload">
            <input
              ref="logoInput"
              type="file"
              accept="image/*"
              class="file-input-hidden"
              @change="handleLogoSelect"
            />
            <template v-if="logoPreview">
              <img :src="logoPreview" alt="Logo preview" class="logo-preview" />
              <span class="upload-change">{{ $t('admin.settings.logo_click_change') }}</span>
            </template>
            <template v-else> {{ $t('admin.settings.logo_upload_hint') }} </template>
          </div>
        </div>
      </div>

      <div class="card">
        <h3 class="card-title">{{ $t('admin.settings.registration_title') }}</h3>
        <div class="toggle-row">
          <div>
            <div class="toggle-label">{{ $t('admin.settings.registration_enabled_label') }}</div>
            <div class="toggle-desc">{{ $t('admin.settings.registration_enabled_desc') }}</div>
          </div>
          <label class="toggle">
            <input v-model="form.registration_enabled" type="checkbox" />
            <span class="toggle-slider"></span>
          </label>
        </div>
        <div class="toggle-row">
          <div>
            <div class="toggle-label">{{ $t('admin.settings.maintenance_label') }}</div>
            <div class="toggle-desc">{{ $t('admin.settings.maintenance_desc') }}</div>
          </div>
          <label class="toggle">
            <input v-model="form.maintenance_mode" type="checkbox" />
            <span class="toggle-slider"></span>
          </label>
        </div>
        <div v-if="form.maintenance_mode" class="form-group" style="margin-top: 16px">
          <label class="form-label">{{ $t('admin.settings.maintenance_message_label') }}</label>
          <textarea
            v-model="form.maintenance_message"
            class="form-input form-textarea"
            rows="3"
            placeholder="We are currently performing scheduled maintenance. Please check back soon."
          ></textarea>
        </div>
        <div class="form-group" style="margin-top: 16px">
          <label class="form-label">{{ $t('admin.settings.session_timeout_label') }}</label>
          <input
            v-model.number="form.session_timeout"
            type="number"
            class="form-input form-input-sm"
            min="5"
            max="480"
            placeholder="30"
          />
          <span class="form-hint">{{ $t('admin.settings.hint_session_timeout') }}</span>
        </div>
      </div>

      <div class="card-footer">
        <button class="btn btn-secondary" :disabled="saving" @click="resetForm">{{ $t('admin.settings.btn_cancel') }}</button>
        <button class="btn btn-primary" :disabled="saving" @click="saveSettings('general')">
          {{ saving ? $t('admin.settings.btn_saving') : $t('admin.settings.btn_save') }}
        </button>
      </div>
      <div v-if="saveMessage" class="save-feedback" :class="saveError ? 'error' : 'success'">
        {{ saveMessage }}
      </div>
    </div>

    <!-- Payments Configuration -->
    <div v-else-if="activeTab === 'payments'">
      <PaymentMethodsConfig />
    </div>

    <!-- Responsible Gaming Tab -->
    <div v-else-if="activeTab === 'responsible-gaming'">
      <div class="card">
        <h3 class="card-title">{{ $t('admin.settings.rg_title') }}</h3>
        <p class="card-desc">
          {{ $t('admin.settings.rg_desc') }}
        </p>
        <div class="form-grid-3">
          <div class="form-group">
            <label class="form-label">{{ $t('admin.settings.daily_limit_label') }}</label>
            <input
              v-model.number="form.deposit_limit_daily_display"
              type="number"
              class="form-input"
              min="0"
              step="1"
              placeholder="500"
            />
          </div>
          <div class="form-group">
            <label class="form-label">{{ $t('admin.settings.weekly_limit_label') }}</label>
            <input
              v-model.number="form.deposit_limit_weekly_display"
              type="number"
              class="form-input"
              min="0"
              step="1"
              placeholder="2000"
            />
          </div>
          <div class="form-group">
            <label class="form-label">{{ $t('admin.settings.monthly_limit_label') }}</label>
            <input
              v-model.number="form.deposit_limit_monthly_display"
              type="number"
              class="form-input"
              min="0"
              step="1"
              placeholder="5000"
            />
          </div>
        </div>
        <span class="form-hint">{{ $t('admin.settings.hint_zero_no_limit') }}</span>
      </div>

      <div class="card">
        <h3 class="card-title">{{ $t('admin.settings.session_reminders_title') }}</h3>
        <div class="form-group">
          <label class="form-label">{{ $t('admin.settings.session_reminder_interval_label') }}</label>
          <input
            v-model.number="form.session_reminder_interval"
            type="number"
            class="form-input form-input-sm"
            min="0"
            max="480"
            placeholder="60"
          />
          <span class="form-hint"
            >{{ $t('admin.settings.hint_session_reminder') }}</span
          >
        </div>
      </div>

      <div class="card">
        <h3 class="card-title">{{ $t('admin.settings.self_exclusion_title') }}</h3>
        <p class="card-desc">{{ $t('admin.settings.self_exclusion_desc') }}</p>
        <div class="checkbox-grid">
          <label v-for="period in selfExclusionOptions" :key="period.value" class="checkbox-row">
            <input
              v-model="form.self_exclusion_periods"
              type="checkbox"
              :value="period.value"
              class="checkbox-input"
            />
            <span class="checkbox-label">{{ period.label }}</span>
          </label>
        </div>
      </div>

      <div class="card">
        <h3 class="card-title">{{ $t('admin.settings.cool_off_title') }}</h3>
        <p class="card-desc">
          {{ $t('admin.settings.cool_off_desc') }}
        </p>
        <div class="checkbox-grid">
          <label v-for="period in coolOffOptions" :key="period.value" class="checkbox-row">
            <input
              v-model="form.cool_off_periods"
              type="checkbox"
              :value="period.value"
              class="checkbox-input"
            />
            <span class="checkbox-label">{{ period.label }}</span>
          </label>
        </div>
        <div class="form-grid" style="margin-top: 20px">
          <div class="form-group">
            <label class="form-label">{{ $t('admin.settings.cool_off_min_weeks') }}</label>
            <input
              v-model.number="form.cool_off_min_weeks"
              type="number"
              class="form-input form-input-sm"
              min="1"
              max="26"
              placeholder="1"
            />
            <span class="form-hint">{{ $t('admin.settings.hint_cool_off_min') }}</span>
          </div>
          <div class="form-group">
            <label class="form-label">{{ $t('admin.settings.cool_off_max_weeks') }}</label>
            <input
              v-model.number="form.cool_off_max_weeks"
              type="number"
              class="form-input form-input-sm"
              min="1"
              max="52"
              placeholder="6"
            />
            <span class="form-hint">{{ $t('admin.settings.hint_cool_off_max') }}</span>
          </div>
        </div>
      </div>

      <div class="card">
        <h3 class="card-title">{{ $t('admin.settings.limit_cooldown_title') }}</h3>
        <p class="card-desc">
          {{ $t('admin.settings.limit_cooldown_desc') }}
        </p>
        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">{{ $t('admin.settings.loss_limit_cooldown_label') }}</label>
            <input
              v-model.number="form.loss_limit_cooldown_hours"
              type="number"
              class="form-input form-input-sm"
              min="0"
              max="336"
              placeholder="24"
            />
            <span class="form-hint">{{ $t('admin.settings.hint_loss_limit_cooldown') }}</span>
          </div>
          <div class="form-group">
            <label class="form-label">{{ $t('admin.settings.wager_limit_cooldown_label') }}</label>
            <input
              v-model.number="form.wager_limit_cooldown_hours"
              type="number"
              class="form-input form-input-sm"
              min="0"
              max="336"
              placeholder="24"
            />
            <span class="form-hint">{{ $t('admin.settings.hint_wager_limit_cooldown') }}</span>
          </div>
        </div>
      </div>

      <div class="card-footer">
        <button class="btn btn-secondary" :disabled="saving" @click="resetForm">{{ $t('admin.settings.btn_cancel') }}</button>
        <button
          class="btn btn-primary"
          :disabled="saving"
          @click="saveSettings('responsible-gaming')"
        >
          {{ saving ? $t('admin.settings.btn_saving') : $t('admin.settings.btn_save') }}
        </button>
      </div>
      <div v-if="saveMessage" class="save-feedback" :class="saveError ? 'error' : 'success'">
        {{ saveMessage }}
      </div>
    </div>

    <!-- Email Tab -->
    <div v-else-if="activeTab === 'email'">
      <div class="card">
        <h3 class="card-title">{{ $t('admin.settings.smtp_title') }}</h3>
        <p class="card-desc">
          {{ $t('admin.settings.smtp_desc') }}
        </p>
        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">{{ $t('admin.settings.smtp_host_label') }}</label>
            <input
              v-model="emailForm.smtp_host"
              type="text"
              class="form-input"
              placeholder="smtp.example.com"
            />
          </div>
          <div class="form-group">
            <label class="form-label">{{ $t('admin.settings.smtp_port_label') }}</label>
            <input
              v-model.number="emailForm.smtp_port"
              type="number"
              class="form-input"
              min="1"
              max="65535"
              placeholder="587"
            />
          </div>
        </div>
        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">{{ $t('admin.settings.smtp_username_label') }}</label>
            <input
              v-model="emailForm.smtp_username"
              type="text"
              class="form-input"
              placeholder="user@example.com"
            />
          </div>
          <div class="form-group">
            <label class="form-label">{{ $t('admin.settings.smtp_password_label') }}</label>
            <input
              v-model="emailForm.smtp_password"
              type="password"
              class="form-input"
              placeholder="••••••••"
            />
            <span class="form-hint">{{ $t('admin.settings.hint_password') }}</span>
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">{{ $t('admin.settings.smtp_encryption_label') }}</label>
          <select v-model="emailForm.smtp_encryption" class="form-input form-input-sm">
            <option value="none">None</option>
            <option value="tls">TLS</option>
            <option value="ssl">SSL</option>
          </select>
        </div>
      </div>

      <div class="card">
        <h3 class="card-title">{{ $t('admin.settings.sender_title') }}</h3>
        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">{{ $t('admin.settings.from_name_label') }}</label>
            <input
              v-model="emailForm.mail_from_name"
              type="text"
              class="form-input"
              placeholder="My Casino"
            />
            <span class="form-hint">{{ $t('admin.settings.hint_from_name') }}</span>
          </div>
          <div class="form-group">
            <label class="form-label">{{ $t('admin.settings.from_email_label') }}</label>
            <input
              v-model="emailForm.mail_from_email"
              type="text"
              class="form-input"
              placeholder="noreply@example.com"
            />
            <span class="form-hint">{{ $t('admin.settings.hint_from_email') }}</span>
          </div>
        </div>
      </div>

      <div class="card">
        <h3 class="card-title">{{ $t('admin.settings.test_email_title') }}</h3>
        <p class="card-desc">
          {{ $t('admin.settings.test_email_desc') }}
        </p>
        <button
          class="btn btn-secondary"
          :disabled="testingEmail"
          @click="sendTestEmail"
        >
          {{ testingEmail ? $t('admin.settings.btn_sending_test') : $t('admin.settings.btn_send_test') }}
        </button>
        <div v-if="testEmailMessage" class="save-feedback" :class="testEmailError ? 'error' : 'success'" style="margin-top: 12px;">
          {{ testEmailMessage }}
        </div>
      </div>

      <div class="card-footer">
        <button class="btn btn-secondary" :disabled="saving" @click="resetEmailForm">{{ $t('admin.settings.btn_cancel') }}</button>
        <button class="btn btn-primary" :disabled="saving" @click="saveSettings('email')">
          {{ saving ? $t('admin.settings.btn_saving') : $t('admin.settings.btn_save') }}
        </button>
      </div>
      <div v-if="saveMessage" class="save-feedback" :class="saveError ? 'error' : 'success'">
        {{ saveMessage }}
      </div>
    </div>

    <!-- Security Tab -->
    <div v-else-if="activeTab === 'security'">
      <div class="card">
        <h3 class="card-title">{{ $t('admin.settings.rate_limiting_title') }}</h3>
        <div class="form-group">
          <label class="form-label">{{ $t('admin.settings.requests_per_minute_label') }}</label>
          <input
            v-model.number="securityForm.rate_limit_per_minute"
            type="number"
            class="form-input form-input-sm"
            min="10"
            max="1000"
            placeholder="60"
          />
          <span class="form-hint">{{ $t('admin.settings.hint_rate_limit') }}</span>
        </div>
      </div>

      <div class="card">
        <h3 class="card-title">{{ $t('admin.settings.ip_whitelist_title') }}</h3>
        <p class="card-desc">
          {{ $t('admin.settings.ip_whitelist_desc') }}
        </p>
        <div class="form-group">
          <textarea
            v-model="securityForm.admin_ip_whitelist"
            class="form-input form-textarea form-mono"
            rows="5"
            placeholder="192.168.1.1&#10;10.0.0.0/24&#10;203.0.113.50"
          ></textarea>
        </div>
      </div>

      <div class="card">
        <h3 class="card-title">{{ $t('admin.settings.twofa_title') }}</h3>
        <div class="toggle-row">
          <div>
            <div class="toggle-label">{{ $t('admin.settings.enforce_2fa_label') }}</div>
            <div class="toggle-desc">{{ $t('admin.settings.enforce_2fa_desc') }}</div>
          </div>
          <label class="toggle">
            <input v-model="securityForm.enforce_2fa" type="checkbox" />
            <span class="toggle-slider"></span>
          </label>
        </div>
      </div>

      <div class="card">
        <h3 class="card-title">{{ $t('admin.settings.geo_blocking_title') }}</h3>
        <p class="card-desc">
          {{ $t('admin.settings.geo_blocking_desc') }}
        </p>
        <div class="geo-search-group">
          <input
            v-model="geoSearch"
            type="text"
            class="form-input"
            :placeholder="$t('admin.settings.geo_search_placeholder')"
          />
        </div>
        <div class="country-grid">
          <label
            v-for="country in filteredCountries"
            :key="country.code"
            class="checkbox-row"
          >
            <input
              v-model="securityForm.geo_blocked_countries"
              type="checkbox"
              :value="country.code"
              class="checkbox-input"
            />
            <span class="checkbox-label">{{ country.name }}</span>
          </label>
        </div>
        <div v-if="securityForm.geo_blocked_countries.length > 0" class="blocked-count">
          {{ $t('admin.settings.geo_blocked_count', { n: securityForm.geo_blocked_countries.length }, securityForm.geo_blocked_countries.length) }}
        </div>
      </div>

      <div class="card-footer">
        <button class="btn btn-secondary" :disabled="saving" @click="resetSecurityForm">{{ $t('admin.settings.btn_cancel') }}</button>
        <button class="btn btn-primary" :disabled="saving" @click="saveSettings('security')">
          {{ saving ? $t('admin.settings.btn_saving') : $t('admin.settings.btn_save') }}
        </button>
      </div>
      <div v-if="saveMessage" class="save-feedback" :class="saveError ? 'error' : 'success'">
        {{ saveMessage }}
      </div>
    </div>

    <!-- Version footer -->
    <div class="settings-version-footer">
      OnlineCasinoScript v{{ appVersion }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'
import { useAdminStore } from '@/stores/admin'
import type { LicenseStatus, UpdateCheckResult } from '@/types/admin'
import ThemeAdminConfig from '@/components/admin/ThemeAdminConfig.vue'
import PaymentMethodsConfig from '@/components/admin/PaymentMethodsConfig.vue'

const { t } = useI18n()

const tabs = computed(() => [
  { id: 'license', label: t('admin.settings.tab_license') },
  { id: 'theme', label: t('admin.settings.tab_theme') },
  { id: 'general', label: t('admin.settings.tab_general') },
  { id: 'payments', label: t('admin.settings.tab_payments') },
  { id: 'responsible-gaming', label: t('admin.settings.tab_responsible_gaming') },
  { id: 'email', label: t('admin.settings.tab_email') },
  { id: 'security', label: t('admin.settings.tab_security') },
])

const activeTab = ref('license')
const loading = ref(false)
const saving = ref(false)
const saveMessage = ref('')
const saveError = ref(false)
const settingsLoaded = ref(false)
const logoInput = ref<HTMLInputElement | null>(null)
const logoPreview = ref<string | null>(null)

// --- License state ---
const toast = useToastStore()
const adminStore = useAdminStore()
const licenseStatus = ref<LicenseStatus | null>(null)
const licenseLoading = ref(false)
const licenseKeyInput = ref('')
const activating = ref(false)
const verifying = ref(false)
const deactivating = ref(false)
const licenseError = ref('')
const showDeactivateConfirm = ref(false)
const updateCheckResult = ref<UpdateCheckResult | null>(null)

const appVersion = computed(
  () =>
    (import.meta.env.VITE_APP_VERSION as string | undefined) ||
    updateCheckResult.value?.current_version ||
    '1.0.0',
)

const selfExclusionOptions = computed(() => [
  { value: '24h', label: t('admin.settings.self_ex_24h') },
  { value: '7d', label: t('admin.settings.self_ex_7d') },
  { value: '30d', label: t('admin.settings.self_ex_30d') },
  { value: '6mo', label: t('admin.settings.self_ex_6mo') },
  { value: '1yr', label: t('admin.settings.self_ex_1yr') },
  { value: 'permanent', label: t('admin.settings.self_ex_permanent') },
])

const coolOffOptions = computed(() => [
  { value: '1h', label: t('admin.settings.cool_1h') },
  { value: '6h', label: t('admin.settings.cool_6h') },
  { value: '24h', label: t('admin.settings.cool_24h') },
  { value: '7d', label: t('admin.settings.cool_7d') },
])

interface SettingsForm {
  // General
  site_name: string
  site_description: string
  site_og_image: string
  default_currency: string
  registration_enabled: boolean
  maintenance_mode: boolean
  maintenance_message: string
  session_timeout: number
  minimum_age: number
  // Responsible Gaming
  deposit_limit_daily_display: number
  deposit_limit_weekly_display: number
  deposit_limit_monthly_display: number
  session_reminder_interval: number
  self_exclusion_periods: string[]
  cool_off_periods: string[]
  loss_limit_cooldown_hours: number
  wager_limit_cooldown_hours: number
  cool_off_min_weeks: number
  cool_off_max_weeks: number
}

const defaultForm: SettingsForm = {
  site_name: '',
  site_description: '',
  site_og_image: '',
  default_currency: 'USD',
  registration_enabled: true,
  maintenance_mode: false,
  maintenance_message: '',
  session_timeout: 30,
  minimum_age: 18,
  deposit_limit_daily_display: 0,
  deposit_limit_weekly_display: 0,
  deposit_limit_monthly_display: 0,
  session_reminder_interval: 60,
  self_exclusion_periods: ['24h', '7d', '30d', '6mo', '1yr', 'permanent'],
  cool_off_periods: ['1h', '6h', '24h', '7d'],
  loss_limit_cooldown_hours: 24,
  wager_limit_cooldown_hours: 24,
  cool_off_min_weeks: 1,
  cool_off_max_weeks: 6,
}

const form = reactive<SettingsForm>({ ...defaultForm })

// Original values for cancel/reset
let originalValues: SettingsForm = { ...defaultForm }

function switchTab(tabId: string) {
  activeTab.value = tabId
  clearFeedback()
  if (
    !settingsLoaded.value &&
    (tabId === 'general' || tabId === 'responsible-gaming' || tabId === 'email' || tabId === 'security')
  ) {
    loadSettings()
  }
  if (tabId === 'license') {
    void loadLicenseStatus()
  }
}

function clearFeedback() {
  saveMessage.value = ''
  saveError.value = false
}

async function loadSettings() {
  if (settingsLoaded.value) return
  loading.value = true
  try {
    const response = await apiClient.get('/admin/api/settings')
    const data = response.data.data || {}
    applySettingsToForm(data)
    settingsLoaded.value = true
  } catch {
    saveMessage.value = t('admin.settings.error_load_settings')
    saveError.value = true
  } finally {
    loading.value = false
  }
}

function applySettingsToForm(data: Record<string, unknown>) {
  // General
  form.site_name = (data.site_name as string) || ''
  form.site_description = (data.site_description as string) || ''
  form.site_og_image = (data.site_og_image as string) || ''
  form.default_currency = (data.default_currency as string) || 'USD'
  form.registration_enabled = data.registration_enabled !== undefined ? !!data.registration_enabled : true
  form.maintenance_mode = !!data.maintenance_mode
  form.maintenance_message = (data.maintenance_message as string) || ''
  form.session_timeout = (data.session_timeout as number) || 30
  form.minimum_age = (data.minimum_age as number) || 18

  // Responsible Gaming — stored as cents, display as dollars
  form.deposit_limit_daily_display = Math.round(((data.deposit_limit_daily as number) || 0) / 100)
  form.deposit_limit_weekly_display = Math.round(((data.deposit_limit_weekly as number) || 0) / 100)
  form.deposit_limit_monthly_display = Math.round(((data.deposit_limit_monthly as number) || 0) / 100)
  form.session_reminder_interval = (data.session_reminder_interval as number) || 60
  form.self_exclusion_periods = Array.isArray(data.self_exclusion_periods)
    ? (data.self_exclusion_periods as string[])
    : ['24h', '7d', '30d', '6mo', '1yr', 'permanent']
  form.cool_off_periods = Array.isArray(data.cool_off_periods)
    ? (data.cool_off_periods as string[])
    : ['1h', '6h', '24h', '7d']
  form.loss_limit_cooldown_hours = (data.loss_limit_cooldown_hours as number) || 24
  form.wager_limit_cooldown_hours = (data.wager_limit_cooldown_hours as number) || 24
  form.cool_off_min_weeks = (data.cool_off_min_weeks as number) || 1
  form.cool_off_max_weeks = (data.cool_off_max_weeks as number) || 6

  // Snapshot original values for reset
  originalValues = { ...form, self_exclusion_periods: [...form.self_exclusion_periods], cool_off_periods: [...form.cool_off_periods] }

  // Email
  emailForm.smtp_host = (data.smtp_host as string) || ''
  emailForm.smtp_port = (data.smtp_port as number) || 587
  emailForm.smtp_username = (data.smtp_username as string) || ''
  emailForm.smtp_password = '' // Never pre-fill password from server
  emailForm.smtp_encryption = (data.smtp_encryption as string) || 'tls'
  emailForm.mail_from_name = (data.mail_from_name as string) || ''
  emailForm.mail_from_email = (data.mail_from_email as string) || ''
  originalEmailValues = { ...emailForm }

  // Security
  securityForm.rate_limit_per_minute = (data.rate_limit_per_minute as number) || 60
  securityForm.admin_ip_whitelist = (data.admin_ip_whitelist as string) || ''
  securityForm.enforce_2fa = !!data.enforce_2fa
  securityForm.geo_blocked_countries = Array.isArray(data.geo_blocked_countries)
    ? (data.geo_blocked_countries as string[])
    : []
  originalSecurityValues = { ...securityForm, geo_blocked_countries: [...securityForm.geo_blocked_countries] }
}

function resetForm() {
  Object.assign(form, {
    ...originalValues,
    self_exclusion_periods: [...originalValues.self_exclusion_periods],
    cool_off_periods: [...originalValues.cool_off_periods],
  })
  clearFeedback()
}

function triggerLogoUpload() {
  logoInput.value?.click()
}

function handleLogoSelect(event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return
  const reader = new FileReader()
  reader.onload = (e) => {
    logoPreview.value = e.target?.result as string
  }
  reader.readAsDataURL(file)
}

// --- Email form ---
interface EmailForm {
  smtp_host: string
  smtp_port: number
  smtp_username: string
  smtp_password: string
  smtp_encryption: string
  mail_from_name: string
  mail_from_email: string
}

const defaultEmailForm: EmailForm = {
  smtp_host: '',
  smtp_port: 587,
  smtp_username: '',
  smtp_password: '',
  smtp_encryption: 'tls',
  mail_from_name: '',
  mail_from_email: '',
}

const emailForm = reactive<EmailForm>({ ...defaultEmailForm })
let originalEmailValues: EmailForm = { ...defaultEmailForm }

const testingEmail = ref(false)
const testEmailMessage = ref('')
const testEmailError = ref(false)

function resetEmailForm() {
  Object.assign(emailForm, { ...originalEmailValues })
  clearFeedback()
}

async function sendTestEmail() {
  testingEmail.value = true
  testEmailMessage.value = ''
  testEmailError.value = false
  try {
    const response = await apiClient.post('/admin/api/settings/test-email')
    testEmailMessage.value = response.data.message || t('admin.settings.test_email_success')
    testEmailError.value = false
  } catch (e: unknown) {
    const axiosError = e as { response?: { data?: { message?: string } } }
    testEmailMessage.value = axiosError.response?.data?.message || t('admin.settings.test_email_error')
    testEmailError.value = true
  } finally {
    testingEmail.value = false
  }
}

// --- Security form ---
interface SecurityForm {
  rate_limit_per_minute: number
  admin_ip_whitelist: string
  enforce_2fa: boolean
  geo_blocked_countries: string[]
}

const defaultSecurityForm: SecurityForm = {
  rate_limit_per_minute: 60,
  admin_ip_whitelist: '',
  enforce_2fa: false,
  geo_blocked_countries: [],
}

const securityForm = reactive<SecurityForm>({ ...defaultSecurityForm })
let originalSecurityValues: SecurityForm = { ...defaultSecurityForm, geo_blocked_countries: [] }

const geoSearch = ref('')

const countries = [
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AL', name: 'Albania' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'AD', name: 'Andorra' },
  { code: 'AO', name: 'Angola' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BZ', name: 'Belize' },
  { code: 'BJ', name: 'Benin' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BR', name: 'Brazil' },
  { code: 'BN', name: 'Brunei' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'KH', name: 'Cambodia' },
  { code: 'CM', name: 'Cameroon' },
  { code: 'CA', name: 'Canada' },
  { code: 'CL', name: 'Chile' },
  { code: 'CN', name: 'China' },
  { code: 'CO', name: 'Colombia' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CU', name: 'Cuba' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DK', name: 'Denmark' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'EG', name: 'Egypt' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'EE', name: 'Estonia' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'GE', name: 'Georgia' },
  { code: 'DE', name: 'Germany' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GR', name: 'Greece' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IR', name: 'Iran' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IL', name: 'Israel' },
  { code: 'IT', name: 'Italy' },
  { code: 'JM', name: 'Jamaica' },
  { code: 'JP', name: 'Japan' },
  { code: 'JO', name: 'Jordan' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KP', name: 'North Korea' },
  { code: 'KR', name: 'South Korea' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'LY', name: 'Libya' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MO', name: 'Macau' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MT', name: 'Malta' },
  { code: 'MX', name: 'Mexico' },
  { code: 'MD', name: 'Moldova' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MN', name: 'Mongolia' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'MA', name: 'Morocco' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'NP', name: 'Nepal' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'NO', name: 'Norway' },
  { code: 'OM', name: 'Oman' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PA', name: 'Panama' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'PE', name: 'Peru' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RO', name: 'Romania' },
  { code: 'RU', name: 'Russia' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'RS', name: 'Serbia' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'ES', name: 'Spain' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'SY', name: 'Syria' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'TH', name: 'Thailand' },
  { code: 'TN', name: 'Tunisia' },
  { code: 'TR', name: 'Turkey' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'UZ', name: 'Uzbekistan' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'YE', name: 'Yemen' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' },
]

const filteredCountries = computed(() => {
  if (!geoSearch.value.trim()) return countries
  const q = geoSearch.value.toLowerCase()
  return countries.filter(
    (c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q),
  )
})

function resetSecurityForm() {
  Object.assign(securityForm, {
    ...originalSecurityValues,
    geo_blocked_countries: [...originalSecurityValues.geo_blocked_countries],
  })
  clearFeedback()
}

async function saveSettings(tab: string) {
  saving.value = true
  clearFeedback()

  try {
    let payload: Record<string, unknown> = {}

    if (tab === 'general') {
      payload = {
        site_name: form.site_name,
        site_description: form.site_description,
        site_og_image: form.site_og_image,
        default_currency: form.default_currency,
        registration_enabled: form.registration_enabled,
        maintenance_mode: form.maintenance_mode,
        maintenance_message: form.maintenance_message,
        session_timeout: form.session_timeout,
        minimum_age: form.minimum_age,
      }
    } else if (tab === 'responsible-gaming') {
      payload = {
        deposit_limit_daily: form.deposit_limit_daily_display * 100,
        deposit_limit_weekly: form.deposit_limit_weekly_display * 100,
        deposit_limit_monthly: form.deposit_limit_monthly_display * 100,
        session_reminder_interval: form.session_reminder_interval,
        self_exclusion_periods: form.self_exclusion_periods,
        cool_off_periods: form.cool_off_periods,
        loss_limit_cooldown_hours: form.loss_limit_cooldown_hours,
        wager_limit_cooldown_hours: form.wager_limit_cooldown_hours,
        cool_off_min_weeks: form.cool_off_min_weeks,
        cool_off_max_weeks: form.cool_off_max_weeks,
      }
    } else if (tab === 'email') {
      payload = {
        smtp_host: emailForm.smtp_host,
        smtp_port: emailForm.smtp_port,
        smtp_username: emailForm.smtp_username,
        smtp_encryption: emailForm.smtp_encryption,
        mail_from_name: emailForm.mail_from_name,
        mail_from_email: emailForm.mail_from_email,
      }
      // Only include password if the user typed a new one
      if (emailForm.smtp_password) {
        payload.smtp_password = emailForm.smtp_password
      }
    } else if (tab === 'security') {
      payload = {
        rate_limit_per_minute: securityForm.rate_limit_per_minute,
        admin_ip_whitelist: securityForm.admin_ip_whitelist,
        enforce_2fa: securityForm.enforce_2fa,
        geo_blocked_countries: securityForm.geo_blocked_countries,
      }
    }

    await apiClient.put('/admin/api/settings', payload)

    // Update original values snapshot for the saved tab
    if (tab === 'general' || tab === 'responsible-gaming') {
      originalValues = { ...form, self_exclusion_periods: [...form.self_exclusion_periods], cool_off_periods: [...form.cool_off_periods] }
    } else if (tab === 'email') {
      emailForm.smtp_password = '' // Clear password field after save
      originalEmailValues = { ...emailForm }
    } else if (tab === 'security') {
      originalSecurityValues = { ...securityForm, geo_blocked_countries: [...securityForm.geo_blocked_countries] }
    }

    saveMessage.value = t('admin.settings.success_save')
    saveError.value = false
  } catch (e: unknown) {
    const axiosError = e as { response?: { data?: { message?: string } } }
    saveMessage.value = axiosError.response?.data?.message || t('admin.settings.error_save')
    saveError.value = true
  } finally {
    saving.value = false
  }
}

// --- License functions ---
async function loadLicenseStatus() {
  licenseLoading.value = true
  licenseError.value = ''
  try {
    const response = await apiClient.get('/admin/api/license/status')
    licenseStatus.value = response.data as LicenseStatus
    adminStore.licenseStatus = licenseStatus.value
    // Check for updates when license is active (non-blocking)
    if (licenseStatus.value.status === 'active') {
      void loadUpdateCheck()
    } else {
      updateCheckResult.value = null
    }
  } catch {
    licenseStatus.value = null
  } finally {
    licenseLoading.value = false
  }
}

async function loadUpdateCheck() {
  try {
    const response = await apiClient.get('/admin/api/license/update-check')
    updateCheckResult.value = response.data as UpdateCheckResult
  } catch {
    // Update check is non-critical — fail silently
    updateCheckResult.value = null
  }
}

async function activateLicense() {
  if (!licenseKeyInput.value.trim()) return
  activating.value = true
  licenseError.value = ''
  try {
    const response = await apiClient.post('/admin/api/license/activate', {
      license_key: licenseKeyInput.value.trim(),
    })
    const data = response.data as { success: boolean; message?: string }
    if (data.success) {
      toast.success(data.message || t('admin.settings.toast_license_activated'))
      licenseKeyInput.value = ''
      await loadLicenseStatus()
    } else {
      licenseError.value = data.message || t('admin.settings.error_license_activation')
    }
  } catch (e: unknown) {
    const axiosError = e as { response?: { data?: { message?: string } } }
    licenseError.value =
      axiosError.response?.data?.message || t('admin.settings.error_license_activation')
  } finally {
    activating.value = false
  }
}

async function verifyLicense() {
  verifying.value = true
  try {
    const response = await apiClient.post('/admin/api/license/verify')
    const data = response.data as { success: boolean; message?: string }
    if (data.success) {
      toast.success(t('admin.settings.toast_license_verified'))
    } else {
      toast.warning(data.message || t('admin.settings.error_license_verification'))
    }
    await loadLicenseStatus()
  } catch {
    toast.error(t('admin.settings.error_license_verification'))
  } finally {
    verifying.value = false
  }
}

async function deactivateLicense() {
  deactivating.value = true
  try {
    const response = await apiClient.post('/admin/api/license/deactivate')
    const data = response.data as { success: boolean; message?: string }
    if (data.success) {
      toast.success(t('admin.settings.toast_license_deactivated'))
    } else {
      toast.error(data.message || t('admin.settings.error_license_deactivation'))
    }
    showDeactivateConfirm.value = false
    await loadLicenseStatus()
  } catch {
    toast.error(t('admin.settings.error_license_deactivation'))
    showDeactivateConfirm.value = false
  } finally {
    deactivating.value = false
  }
}

onMounted(() => {
  if (activeTab.value === 'license') {
    void loadLicenseStatus()
  }
  // Pre-load settings if starting on a settings-backed tab
  if (['general', 'responsible-gaming', 'email', 'security'].includes(activeTab.value)) {
    loadSettings()
  }
})
</script>

<style scoped>
.settings-page {
  padding: var(--space-6);
}

.settings-version-footer {
  margin-top: var(--space-8);
  padding-top: var(--space-4);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 11px;
  color: var(--text-secondary);
  text-align: center;
  opacity: 0.6;
}

.settings-header {
  margin-bottom: var(--space-6);
}

.page-title {
  font-size: var(--font-size-h1);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--space-1);
}

.page-subtitle {
  font-size: var(--font-size-body);
  color: var(--text-secondary);
  margin: 0;
}

.settings-tabs {
  display: flex;
  gap: var(--space-2);
  border-bottom: 1px solid var(--bg-hover);
  margin-bottom: var(--space-6);
  overflow-x: auto;
}

.settings-tab {
  padding: var(--space-3) var(--space-5);
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--text-secondary);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  white-space: nowrap;
  transition: all var(--transition-fast);
  min-height: 44px;
}

.settings-tab.active {
  color: var(--accent-primary);
  border-bottom-color: var(--accent-primary);
}

.settings-tab:hover:not(.active) {
  color: var(--text-primary);
}

/* Cards */
.card {
  background: var(--bg-secondary);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: var(--radius-lg);
  padding: 24px;
  margin-bottom: 24px;
}

.card-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  color: var(--text-primary);
}

.card-desc {
  font-size: 13px;
  color: var(--text-tertiary);
  margin: -12px 0 20px;
  line-height: 1.5;
}

/* Form elements */
.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  background: var(--bg-tertiary);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  font-size: 13px;
  font-family: inherit;
  transition: border-color var(--transition-fast);
}

.form-input:focus {
  outline: none;
  border-color: var(--accent-secondary);
}

.form-input::placeholder {
  color: var(--text-tertiary);
}

.form-input-sm {
  max-width: 200px;
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.form-hint {
  display: block;
  font-size: 11px;
  color: var(--text-tertiary);
  margin-top: 6px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-grid-3 {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
}

/* Toggle switch */
.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}

.toggle-row:last-child {
  border-bottom: none;
}

.toggle-label {
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 600;
}

.toggle-desc {
  font-size: 11px;
  color: var(--text-tertiary);
  margin-top: 4px;
}

.toggle {
  position: relative;
  display: inline-flex;
  width: 44px;
  height: 24px;
  background: var(--bg-tertiary);
  border-radius: 9999px;
  cursor: pointer;
  flex-shrink: 0;
}

.toggle input {
  display: none;
}

.toggle-slider {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background: var(--text-tertiary);
  border-radius: 50%;
  transition: all var(--transition-fast);
}

.toggle input:checked + .toggle-slider {
  background: var(--accent-primary);
  left: 22px;
}

/* Checkboxes */
.checkbox-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
}

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: var(--bg-tertiary);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.checkbox-row:hover {
  background: rgba(255, 255, 255, 0.08);
}

.checkbox-input {
  width: 16px;
  height: 16px;
  accent-color: var(--accent-primary);
  cursor: pointer;
  flex-shrink: 0;
}

.checkbox-label {
  font-size: 13px;
  color: var(--text-primary);
}

/* Upload box */
.upload-box {
  border: 2px dashed rgba(255, 255, 255, 0.15);
  border-radius: var(--radius-md);
  padding: 20px;
  text-align: center;
  color: var(--text-tertiary);
  cursor: pointer;
  transition: all var(--transition-fast);
  font-size: 13px;
}

.upload-box:hover {
  border-color: var(--accent-secondary);
  color: var(--text-primary);
}

.file-input-hidden {
  display: none;
}

.logo-preview {
  max-height: 60px;
  max-width: 200px;
  object-fit: contain;
  margin-bottom: 8px;
}

.upload-change {
  display: block;
  font-size: 11px;
  color: var(--text-tertiary);
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 700;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
  white-space: nowrap;
  border: none;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--accent-primary);
  color: #000;
}

.btn-primary:hover:not(:disabled) {
  background: var(--accent-primary-hover);
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
}

.btn-secondary {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
}

/* Footer */
.card-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 0;
  padding-top: 0;
}

/* Feedback */
.save-feedback {
  margin-top: 12px;
  padding: 10px 14px;
  border-radius: var(--radius-sm);
  font-size: 13px;
  font-weight: 500;
}

.save-feedback.success {
  background: rgba(0, 231, 1, 0.1);
  color: var(--accent-primary);
  border: 1px solid rgba(0, 231, 1, 0.2);
}

.save-feedback.error {
  background: rgba(237, 66, 69, 0.1);
  color: #ed4245;
  border: 1px solid rgba(237, 66, 69, 0.2);
}

/* Loading */
.loading-panel {
  background: var(--bg-secondary);
  border-radius: var(--radius-lg);
  padding: var(--space-10);
  text-align: center;
  color: var(--text-tertiary);
  font-size: var(--font-size-body);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid var(--bg-tertiary);
  border-top-color: var(--accent-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Mono textarea for IP whitelist */
.form-mono {
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.6;
}

/* Geo-blocking */
.geo-search-group {
  margin-bottom: 16px;
}

.country-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 4px;
}

.blocked-count {
  margin-top: 12px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: var(--radius-sm);
}

/* Responsive */
@media (max-width: 768px) {
  .form-grid,
  .form-grid-3 {
    grid-template-columns: 1fr;
  }

  .checkbox-grid,
  .country-grid {
    grid-template-columns: 1fr 1fr;
  }

  .license-info-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .checkbox-grid,
  .country-grid {
    grid-template-columns: 1fr;
  }
}

/* License panel */
.license-banner {
  padding: 14px 18px;
  border-radius: var(--radius-md);
  font-size: 13px;
  margin-bottom: 20px;
  line-height: 1.5;
}

.license-banner-warning {
  background: rgba(255, 186, 8, 0.1);
  border: 1px solid rgba(255, 186, 8, 0.3);
  color: #ffba08;
}

.license-banner-error {
  background: rgba(237, 66, 69, 0.1);
  border: 1px solid rgba(237, 66, 69, 0.3);
  color: #ed4245;
}

.license-details-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.license-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: var(--radius-sm);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.badge-active {
  background: rgba(0, 231, 1, 0.15);
  color: var(--accent-primary);
  border: 1px solid rgba(0, 231, 1, 0.3);
}

.badge-grace {
  background: rgba(255, 186, 8, 0.15);
  color: #ffba08;
  border: 1px solid rgba(255, 186, 8, 0.3);
}

.license-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
}

.license-info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.license-info-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-secondary);
}

.license-info-value {
  font-size: 13px;
  color: var(--text-primary);
  font-family: 'JetBrains Mono', 'Courier New', monospace;
}

.license-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.update-notification {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
  padding: 12px 16px;
  background: rgba(88, 101, 242, 0.08);
  border: 1px solid rgba(88, 101, 242, 0.25);
  border-radius: var(--radius-md);
  font-size: 13px;
}

.update-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: var(--radius-sm);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: rgba(88, 101, 242, 0.2);
  color: #8b9cf8;
  border: 1px solid rgba(88, 101, 242, 0.3);
  white-space: nowrap;
}

.update-version {
  font-weight: 600;
  color: var(--text-primary);
  font-family: 'JetBrains Mono', 'Courier New', monospace;
}

.update-download-link {
  color: #8b9cf8;
  text-decoration: underline;
  font-weight: 600;
  white-space: nowrap;
}

.update-download-link:hover {
  color: #a5b4fc;
}

.update-hint {
  color: var(--text-secondary);
  font-size: 12px;
  width: 100%;
}

.btn-danger {
  background: rgba(237, 66, 69, 0.15);
  color: #ed4245;
  border: 1px solid rgba(237, 66, 69, 0.3);
}

.btn-danger:hover:not(:disabled) {
  background: rgba(237, 66, 69, 0.25);
}

/* Deactivate confirmation modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.modal-box {
  background: var(--bg-secondary);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-lg);
  padding: 28px;
  max-width: 480px;
  width: 90%;
}

.modal-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 12px;
}

.modal-text {
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0 0 20px;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}
</style>
